/**
 * Custom Element: MCartDrawerUpsellItems
 * Dynamically fetches and displays product recommendations within the Shopify cart drawer.
 */
class MCartDrawerUpsellItems extends HTMLElement {
    // Array to hold cleanup functions for event subscriptions to prevent memory leaks
    _$Subscriptions = [];

    /**
     * Component constructor initializing basic properties and DOM references.
     */
    constructor() {
        // Initialize the parent HTMLElement class
        super();

        // Get the store's base URL from the Shopify global object
        this.rootUrl = window.Shopify.routes.root;

        // Maximum number of upsell products to show at once
        this.limit = 4;

        // Locate the main cart drawer custom element in the document
        this.cartDrawer = document.querySelector('m-cart-drawer');

        // Reference the first line item in the cart to use for context-aware suggestions
        this.firstCartDrawerItem = this.cartDrawer && this.cartDrawer.querySelector('.m-cart-item');
    }

    /**
     * Fetches product recommendations from Shopify's Recommendation API.
     * @param {string} id - The Product ID to base recommendations on.
     * @param {number} limit - The number of recommendations to return.
     * @returns {Promise<Object>} The JSON response from the API.
     */
    async getRecommendations(id, limit) {
        // Request complementary products via Shopify's AJAX API recommendations endpoint
        return await fetchJSON(`${this.rootUrl}recommendations/products.json?product_id=${id}&limit=${limit}&intent=complementary`);
    }

    /**
     * Initializes the component's event listeners and subscriptions.
     */
    initializeListners() {
        // Bind the logic for the "Add to Cart" buttons
        this.handleAddToCart();

        // Update the list whenever the cart content changes globally
        const cartUpdate$ = window.MinimogEvents.subscribe(MinimogTheme.pubSubEvents.cartUpdate, async (e) => {
            // Trigger a re-render to reflect current cart state
            await this.renderProducts();
        });

        // Refresh items whenever the user opens the side drawer
        const openCartDrawer$ = window.MinimogEvents.subscribe(MinimogTheme.pubSubEvents.openCartDrawer, async (e) => {
            const cartAlert = document.querySelector('#cart-alert');

            const cartElement = document.getElementById('shopify-cart-data');

            if (cartElement) {
                const cartData = JSON.parse(cartElement.innerHTML);
                const cartTotal = cartData.total_price; // Value in cents

                // Define thresholds in cents
                const minThreshold = 10000; // $100
                const maxThreshold = 30000; // $300

                if (cartTotal >= minThreshold && cartTotal < maxThreshold) {
                    cartAlert.style.display = 'block';
                } else {
                    cartAlert.style.display = 'none';
                }
            }

            // Ensure recommendations are up to date upon viewing
            await this.renderProducts();
        });

        // Track the cart update subscription for later removal
        this._$Subscriptions.push(cartUpdate$);

        // Track the drawer open subscription for later removal
        this._$Subscriptions.push(openCartDrawer$);
    }

    /**
     * Sends a POST request to add a product to the Shopify cart.
     * @param {Object} data - The payload containing item variants and quantity.
     * @returns {Promise<Object>} The updated cart JSON data.
     */
    async addToCart(data) {
        // Execute a POST request to Shopify's cart add endpoint
        return await fetchJSON(`${this.rootUrl}cart/add.js`, {
            // Define request type as POST
            method: 'POST',

            // Set required headers for JSON processing
            headers: {
                // Inform the server that the payload is JSON
                'Content-Type': 'application/json',
            },

            // Convert the JavaScript object to a string for transmission
            body: JSON.stringify(data),
        });
    }

    /**
     * Handles the "Add to Cart" button clicks using event delegation.
     */
    handleAddToCart() {
        // Use a delegate helper to listen for clicks on dynamically rendered buttons
        const cleanup = addEventDelegate({
            // Target the specific class used for upsell add-to-cart actions
            selector: '.cart-drawer-upsell-item-add-to-cart-btn',

            // Define the async logic to execute on click
            handler: async (event, button) => {
                // Prevent the button from refreshing the page or submitting a form
                event.preventDefault();

                // Retrieve the variant ID stored in the button's data attribute
                const variantId = button.getAttribute('data-id');

                // Construct the payload for the Shopify AJAX API
                const payload = {
                    // Create an array containing the item to be added
                    items: [
                        {
                            // Parse the string ID into an integer
                            id: parseInt(variantId),

                            // Default quantity is one item
                            quantity: 1,
                        },
                    ],
                };

                // Check if the parent drawer supports partial section rendering
                if (this.cartDrawer && typeof this.cartDrawer.getSectionsToRender === 'function') {
                    // Map out the required section IDs to refresh the UI automatically
                    payload.sections = this.cartDrawer.getSectionsToRender().map((e) => e.id);

                    // Provide the current path to ensure context-aware rendering
                    payload.sections_url = window.location.pathname;
                }

                // Attempt to perform the cart update
                try {
                    // Set button loading
                    button.innerHTML = `
                    <div class="position-absolute top-0 bottom-0 end-0 start-0 w-100 d-flex align-items-center justify-content-center">
                        <span
                        class="spinner-border spinner-border-sm"
                        aria-hidden="true"
                        ></span>
                    </div>
                    <span role="status" style="visibility: hidden">Add to cart</span>
                    `;
                    button.setAttribute('disabled', true);

                    // Call the API helper with the prepared payload
                    const response = await this.addToCart(payload);

                    if (this.cartDrawer && response.sections) {
                        // 1. Update the UI content of the drawer
                        this.cartDrawer.renderContents(response);

                        // 2. EMIT THE EVENT HERE
                        // This notifies the Progress Bar and other elements to refresh
                        window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, {
                            ...response,
                            source: 'cart-drawer-upsell',
                        });
                    }
                } catch (error) {
                    // Log the error to the console for debugging purposes
                    console.error('Failed to add upsell to cart:', error);
                } finally {
                    // Set default button text
                    button.innerHTML = `${window.MinimogStrings.addToCart}`;
                    button.removeAttribute('disabled');
                }
            },
        });

        // Store the delegation cleanup function to prevent memory leaks
        this._$Subscriptions.push({ unsubscribe: cleanup });
    }

    /**
     * Renders the HTML for the upsell product list.
     */
    async renderProducts() {
        // Wrap rendering in a try-block to handle API or DOM failures gracefully
        try {
            // Stop if there are no items in the cart to base recommendations on
            if (!this.firstCartDrawerItem) return;

            // Fetch the unique product ID from the first cart item's attributes
            const productId = this.firstCartDrawerItem.getAttribute('data-product-id');

            // Abort if the cart item doesn't have a valid product ID
            if (!productId) return;

            // Retrieve the list of recommended products from the API
            const { products } = await this.getRecommendations(productId, this.limit);

            // Hide the component if the API returns no results
            if (!products || !products.length) {
                // Clear the element's content
                this.innerHTML = '';

                // End the function execution
                return;
            }

            // Transform the product data into a string of HTML elements
            const productsHTML = products
                .map((product) => {
                    // Only render items that are currently in stock
                    if (!product.available) return '';

                    // Get the URL for the product's primary image
                    const imgSource = product.featured_image;

                    // Build an image tag if a source exists, otherwise use a placeholder
                    const productImg = imgSource
                        ? // Standard image element with responsive classes
                          `<img width="80" height="80" src="${imgSource}" class="rounded-3 object-cover" alt="${product.title}" style="max-height: 80px;"/>`
                        : // Default SVG icon for missing images
                          `<svg width="80" class="m-placeholder-svg rounded-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5"><path d="M324.5 212.7H203c-1.6 0-2.8 1.3-2.8 2.8V308c0 1.6 1.3 2.8 2.8 2.8h121.6c1.6 0 2.8-1.3 2.8-2.8v-92.5c0-1.6-1.3-2.8-2.9-2.8zm1.1 95.3c0 .6-.5 1.1-1.1 1.1H203c-.6 0-1.1-.5-1.1-1.1v-92.5c0-.6.5-1.1 1.1-1.1h121.6c.6 0 1.1.5 1.1 1.1V308z"></path><path d="M210.4 299.5H240v.1s.1 0 .2-.1h75.2v-76.2h-105v76.2zm1.8-7.2l20-20c1.6-1.6 3.8-2.5 6.1-2.5s4.5.9 6.1 2.5l1.5 1.5 16.8 16.8c-12.9 3.3-20.7 6.3-22.8 7.2h-27.7v-5.5zm101.5-10.1c-20.1 1.7-36.7 4.8-49.1 7.9l-16.9-16.9 26.3-26.3c1.6-1.6 3.8-2.5 6.1-2.5s4.5.9 6.1 2.5l27.5 27.5v7.8zm-68.9 15.5c9.7-3.5 33.9-10.9 68.9-13.8v13.8h-68.9zm68.9-72.7v46.8l-26.2-26.2c-1.9-1.9-4.5-3-7.3-3s-5.4 1.1-7.3 3l-26.3 26.3-.9-.9c-1.9-1.9-4.5-3-7.3-3s-5.4 1.1-7.3 3l-18.8 18.8V225h101.4z"></path><path d="M232.8 254c4.6 0 8.3-3.7 8.3-8.3s-3.7-8.3-8.3-8.3-8.3 3.7-8.3 8.3 3.7 8.3 8.3 8.3zm0-14.9c3.6 0 6.6 2.9 6.6 6.6s-2.9 6.6-6.6 6.6-6.6-2.9-6.6-6.6 3-6.6 6.6-6.6z"></path></svg>`;

                    // Generate the template literal for a single product card
                    return `
                        <div class="cart-drawer-upsell-item bg-secondary bg-opacity-50 rounded-3" data-id="${product.id}" style="min-width:260px; max-width: 340px; flex-shrink: 0;">
                            <div class="cart-drawer-upsell-item-content gap-3 p-3 d-flex align-items-start">
                                ${productImg}
                                <div class="d-flex flex-column align-items-start gap-1 overflow-hidden">
                                    <p class="cart-drawer-upsell-item-title fs-6 mb-0 text-truncate w-100">${product.title}</p>
                                    <p class="cart-drawer-upsell-item-price mb-2 small text-muted">$${(product.price / 100).toFixed(2)}</p>
                                    <button data-id="${product.variants[0].id}" type="button" class="position-relative btn btn-sm bg-primary text-white cart-drawer-upsell-item-add-to-cart-btn">
                                        ${window.MinimogStrings.addToCart} 
                                    </button>
                                </div>
                            </div>
                        </div>
                    `;
                })
                .join('');

            // Inject the final container and product list into the custom element
            this.innerHTML = `
                <div class="cart-drawer-upsell">
                    <hr class="mb-3 ">
                    <p class="text-uppercase fw-bold small mb-3 ">You may also like</p>
                    <div class="cart-drawer-upsell-items d-flex gap-3 overflow-x-auto pb-2" style="scrollbar-width: none;">
                        ${productsHTML}
                    </div>
                </div>
            `;
        } catch (error) {
            // Log issues if the recommendation fetch or rendering fails
            console.log('Error in rendering products', error);
        } finally {
        }
    }

    /**
     * Called when the element is inserted into the DOM.
     */
    connectedCallback() {
        // Start the event listeners when the element becomes active in the browser
        this.initializeListners();
    }

    /**
     * Called when the element is removed from the DOM.
     */
    disconnectedCallback() {
        // Loop through all active subscriptions to clean them up
        this._$Subscriptions.forEach((sub) => {
            // Handle cleanup if the subscription has an unsubscribe method
            if (sub && typeof sub.unsubscribe === 'function') sub.unsubscribe();
            // Handle cleanup if the subscription itself is a direct function
            if (typeof sub === 'function') sub();
        });
    }
}

// Check if the custom element is already registered to avoid naming conflicts
if (!customElements.get('m-cart-drawer-upsell-items')) {
    // Register the class as a usable HTML tag in the DOM
    customElements.define('m-cart-drawer-upsell-items', MCartDrawerUpsellItems);
}
